import React, { useState, useEffect } from 'react'

import {
    Card,
    Grid,
    Typography,
    Divider,
    Box,
    Paper,
    IconButton,
    Popover,
    TextField,
    Button,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import EditIcon from '@material-ui/icons/Edit'

import { InternalInvoice } from '../../../models/Invoice/types'
import { formatCurrency, formatDate } from './helpers'

interface InvoiceSummaryProps {
    invoice: InternalInvoice
    onUpdateInvoiceDates?: (
        invoiceId: string,
        issue_date: string,
        due_date: string,
    ) => void
    viewOnly?: boolean
}

export const InvoiceSummary: React.FC<InvoiceSummaryProps> = ({
    invoice,
    onUpdateInvoiceDates,
    viewOnly = false,
}) => {
    const theme = useTheme()
    const [
        editPopoverAnchor,
        setEditPopoverAnchor,
    ] = useState<HTMLElement | null>(null)

    // Convert date to YYYY-MM-DD format for HTML date input
    const formatDateForInput = (dateString: string) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toISOString().split('T')[0]
    }

    const [issueDate, setIssueDate] = useState(
        formatDateForInput(invoice.issue_date),
    )

    // Update local state when invoice changes
    useEffect(() => {
        setIssueDate(formatDateForInput(invoice.issue_date))
    }, [invoice.issue_date])

    const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
        setEditPopoverAnchor(event.currentTarget)
    }

    const handleEditClose = () => {
        setEditPopoverAnchor(null)
        // Reset to original value if cancelled
        setIssueDate(formatDateForInput(invoice.issue_date))
    }

    const handleSave = () => {
        if (onUpdateInvoiceDates) {
            onUpdateInvoiceDates(invoice.id, issueDate, invoice.due_date)
        }
        setEditPopoverAnchor(null)
    }

    const open = Boolean(editPopoverAnchor)

    return (
        <Paper elevation={3} style={{ padding: 12, borderRadius: 16 }}>
            <Box display="flex" alignItems="center" style={{ height: '100%' }}>
                <Grid
                    container
                    spacing={2}
                    alignItems="stretch"
                    style={{ flex: 1 }}
                >
                    {/* Dates Group */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                            Created
                        </Typography>
                        <Typography variant="h6">
                            {formatDate(invoice.created_date)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                            Issued
                        </Typography>
                        <Typography variant="h6">
                            {formatDate(invoice.issue_date)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                            Due
                        </Typography>
                        <Typography variant="h6">
                            {formatDate(invoice.due_date)}
                        </Typography>
                    </Grid>

                    {/* Financials Group */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                            Total Billed
                        </Typography>
                        <Typography variant="h6">
                            {formatCurrency(invoice.total_amount)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                            Total Paid
                        </Typography>
                        <Typography variant="h6">
                            {formatCurrency(invoice.total_paid)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                            Balance Due
                        </Typography>
                        <Typography variant="h6">
                            {formatCurrency(invoice.balance_due)}
                        </Typography>
                    </Grid>
                </Grid>

                {!viewOnly && onUpdateInvoiceDates && (
                    <IconButton
                        size="small"
                        onClick={handleEditClick}
                        style={{ marginLeft: 16 }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            {/* Edit Dates Popover */}
            <Popover
                open={open}
                anchorEl={editPopoverAnchor}
                onClose={handleEditClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box p={2} minWidth={300}>
                    <Typography variant="h6" gutterBottom>
                        Edit Issue Date
                    </Typography>
                    <TextField
                        fullWidth
                        label="Issue Date"
                        type="date"
                        value={issueDate}
                        onChange={(e) => setIssueDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        margin="normal"
                    />
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            onClick={handleEditClose}
                            style={{ marginRight: 8 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </Paper>
    )
}
